import { all, fork, put, takeEvery, call, select } from 'redux-saga/effects';
import * as actionTypes from 'appRedux/actions/taxInvoice/vatInfoActionType';
import * as actionCreators from 'appRedux/actions/taxInvoice/vatInfoActions';
import { userSignOut } from 'appRedux/actions/Auth';
import { API_URL_V1, API_PMS_HEADERS } from 'constants/ApiSetting';
import { APIPostRequest } from 'util/connection';
import { getItems } from 'util/localStorage';
import { AppState } from 'appRedux/reducers/ReducerState';

// Get VAT Info Saga
function* getVatInfoRequestProcess({ payload }: actionTypes.GetVatInfoRequestAction): Generator<any, void, any> {
  try {
    // Check if SALESTAX is enabled before making API call
    const systemConfig: any = yield select((state: AppState) => state.systemConfig);
    const isSalesTaxEnabled = systemConfig?.dataSource?.interfaces?.SALESTAX === true;

    if (!isSalesTaxEnabled) {
      console.log('Get VAT Info API: SALESTAX is disabled, skipping API call');
      // Return empty success response instead of error to prevent UI issues
      yield put(actionCreators.getVatInfoSuccess({
        data: {
          result_code: '0',
          result_msg: 'SALESTAX is disabled',
          msg_code: '00',
          result_data: {
            issue_type: undefined,
            detail: [],
            header: {
              id: null,
              invoice_no: null,
              invoice_date: '',
              money_text_id: 0,
              invoice_remark: null,
              invoice_book_no: ''
            },
            summary: {
              total: 0,
              nonvat: 0,
              vatable: 0,
              zerovat: 0,
              localvat: 0,
              valueaddtax: 0
            },
            customer: {
              e_mail: null,
              cust_name: '',
              buyer_type: '',
              cust_phone: null,
              cust_mobile: null,
              cust_tax_id: null,
              country_code: '',
              cust_address: '',
              cust_branch_no: null,
              cust_branch_type: ''
            },
            interface: {
              room_no: '',
              guest_ref: 0,
              link_total: 0,
              machine_no: null,
              calculate_vat: false,
              interface_from: '',
              document_reference: ''
            },
            payment_type: {
              paybank: false,
              paycard: false,
              paycash: false,
              payother: false,
              paybank_amt: 0,
              paycard_amt: 0,
              paycash_amt: 0,
              paybank_desc: '',
              paycard_desc: '',
              paycash_desc: '',
              payother_amt: 0,
              payother_desc: ''
            }
          }
        },
        status: 200,
        message: 'OK'
      }));
      return;
    }

    // ดึง token จาก localStorage
    const { access_token } = yield call(getItems, ['access_token']);

    if (!access_token) {
      console.error('Get VAT Info API: Access token is missing');
      yield put(actionCreators.getVatInfoError('Authentication token is missing. Please login again.'));
      return;
    }

    // ใช้ API URL สำหรับ get_vat_info
    const url = `${API_URL_V1}pms/rpc/salestax/get_vat_info`;

    // ใช้ API_PMS_HEADERS ตาม pattern ของ sagas อื่นๆ
    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;

    const response: any = yield call(APIPostRequest, url, payload, API_PMS_HEADERS);

    // ตรวจสอบ response structure
    const responseData = response?.data || response;
    const hasResultCode = responseData?.result_code !== undefined;
    const hasResultData = responseData?.result_data !== undefined;
    const resultCode = responseData?.result_code;

    // ตรวจสอบว่า response มี status หรือไม่
    const isSuccess = (response?.status === 200 || !response?.status) && resultCode === '0';

    if (isSuccess && hasResultCode && hasResultData) {
      // สร้าง GetVatInfoResponse structure
      const vatInfoResponse: actionTypes.GetVatInfoResponse = {
        data: {
          result_code: responseData.result_code,
          result_msg: responseData.result_msg || 'Success',
          msg_code: responseData.msg_code || '00',
          result_data: responseData.result_data,
        },
        status: response?.status || 200,
        message: response?.message || 'OK',
      };

      yield put(actionCreators.getVatInfoSuccess(vatInfoResponse));
    } else {
      // Error case
      const errorMessage = responseData?.result_msg || response?.message || 'Failed to get VAT info';

      yield put(actionCreators.getVatInfoError(errorMessage));

      if (response?.status === 403) {
        // Force signout if unauthorized
        yield put(userSignOut());
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    yield put(actionCreators.getVatInfoError(errorMessage, error));
  }
}

// Issue Tax Invoice Saga
function* issueTaxInvoiceRequestProcess({ payload }: actionTypes.IssueTaxInvoiceRequestAction): Generator<any, void, any> {
  try {
    // Check if SALESTAX is enabled before making API call
    const systemConfig: any = yield select((state: AppState) => state.systemConfig);
    const isSalesTaxEnabled = systemConfig?.dataSource?.interfaces?.SALESTAX === true;

    if (!isSalesTaxEnabled) {
      console.log('Issue Tax Invoice API: SALESTAX is disabled, skipping API call');
      yield put(actionCreators.issueTaxInvoiceError('SALESTAX is disabled'));
      return;
    }

    // ดึง token จาก localStorage
    const { access_token } = yield call(getItems, ['access_token']);

    if (!access_token) {
      console.error('Issue Tax Invoice API: Access token is missing');
      yield put(actionCreators.issueTaxInvoiceError('Authentication token is missing. Please login again.'));
      return;
    }

    const { isCreditNote, ...requestPayloadFromAction } = payload as actionTypes.IssueTaxInvoiceRequest;
    const endpoint = isCreditNote ? 'issue_credit_note' : 'issue_tax_invoice';
    const url = `${API_URL_V1}pms/rpc/salestax/${endpoint}`;

    // ใช้ API_PMS_HEADERS ตาม pattern ของ sagas อื่นๆ
    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;

    const hasFolioItem = requestPayloadFromAction.folio_item !== undefined && requestPayloadFromAction.folio_item !== null;

    // Payload should have payment_tran_ids and invoice_data
    const requestPayload: any = {
      invoice_data: requestPayloadFromAction.invoice_data || {}
    };

    if (hasFolioItem) {
      requestPayload.folio_item = requestPayloadFromAction.folio_item;
    } else {
      requestPayload.payment_tran_ids = requestPayloadFromAction.payment_tran_ids || [];
    }

    const response: any = yield call(APIPostRequest, url, requestPayload, API_PMS_HEADERS);

    // ตรวจสอบ response structure
    const responseData = response?.data || response;
    const hasResultCode = responseData?.result_code !== undefined;
    const resultCode = responseData?.result_code;

    // ตรวจสอบว่า response มี status หรือไม่
    const isSuccess = (response?.status === 200 || !response?.status) && resultCode === '0';

    if (isSuccess && hasResultCode) {
      // สร้าง IssueTaxInvoiceResponse structure
      const issueResponse: actionTypes.IssueTaxInvoiceResponse = {
        data: {
          result_code: responseData.result_code,
          result_msg: responseData.result_msg || 'Success',
          msg_code: responseData.msg_code || '00',
          result_data: responseData.result_data,
        },
        status: response?.status || 200,
        message: response?.message || 'OK',
      };

      yield put(actionCreators.issueTaxInvoiceSuccess(issueResponse));
    } else {
      // Error case - but still send response in IssueTaxInvoiceResponse format to preserve result_msg
      if (hasResultCode) {
        // Create IssueTaxInvoiceResponse structure for error response to preserve result_msg
        const errorResponse: actionTypes.IssueTaxInvoiceResponse = {
          data: {
            result_code: responseData.result_code,
            result_msg: responseData.result_msg || response?.message || 'Failed to issue tax invoice',
            msg_code: responseData.msg_code || '99',
            result_data: responseData.result_data || null,
          },
          status: response?.status || 200,
          message: response?.message || 'Error',
        };
        yield put(actionCreators.issueTaxInvoiceSuccess(errorResponse));
      } else {
        // No result_code in response - use error action
        const errorMessage = responseData?.result_msg || response?.message || 'Failed to issue tax invoice';
        yield put(actionCreators.issueTaxInvoiceError(errorMessage));
      }

      if (response?.status === 403) {
        // Force signout if unauthorized
        yield put(userSignOut());
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    yield put(actionCreators.issueTaxInvoiceError(errorMessage, error));
  }
}

// Cancel Tax Invoice Saga
function* cancelTaxInvoiceRequestProcess({ payload }: actionTypes.CancelTaxInvoiceRequestAction): Generator<any, void, any> {
  try {

    // ดึง token จาก localStorage
    const { access_token } = yield call(getItems, ['access_token']);

    if (!access_token) {
      console.error('[Cancel Tax Invoice Saga] Access token is missing');
      yield put(actionCreators.cancelTaxInvoiceError('Authentication token is missing. Please login again.'));
      return;
    }

    // ใช้ API URL สำหรับ cancel_tax_invoice
    const url = `${API_URL_V1}pms/rpc/salestax/cancel_tax_invoice`;

    // ใช้ API_PMS_HEADERS ตาม pattern ของ sagas อื่นๆ
    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;

    // Payload structure: { vat_id: number, remark: string }
    const requestPayload = {
      vat_id: payload.vat_id,
      remark: payload.remark || 'Void payment transaction'
    };


    const response: any = yield call(APIPostRequest, url, requestPayload, API_PMS_HEADERS);

    // ตรวจสอบ response structure
    const responseData = response?.data || response;
    const hasResultCode = responseData?.result_code !== undefined;
    const resultCode = responseData?.result_code;

    // ตรวจสอบว่า response มี status หรือไม่
    const isSuccess = (response?.status === 200 || !response?.status) && resultCode === '0';

    if (isSuccess && hasResultCode) {
      // สร้าง CancelTaxInvoiceResponse structure
      const cancelResponse: actionTypes.CancelTaxInvoiceResponse = {
        data: {
          result_code: responseData.result_code,
          result_msg: responseData.result_msg || 'Success',
          msg_code: responseData.msg_code || '00',
          result_data: responseData.result_data,
        },
        status: response?.status || 200,
        message: response?.message || 'OK',
      };

      yield put(actionCreators.cancelTaxInvoiceSuccess(cancelResponse));
    } else {
      // Error case
      console.error('[Cancel Tax Invoice Saga] Error - creating error response', {
        result_code: resultCode,
        result_msg: responseData?.result_msg,
        message: response?.message,
      });
      const errorMessage = responseData?.result_msg || response?.message || 'Failed to cancel tax invoice';

      yield put(actionCreators.cancelTaxInvoiceError(errorMessage));

      if (response?.status === 403) {
        // Force signout if unauthorized
        yield put(userSignOut());
      }
    }
  } catch (error) {
    console.error('[Cancel Tax Invoice Saga] Exception occurred', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    yield put(actionCreators.cancelTaxInvoiceError(errorMessage, error));
  }
}

function* getVatInfoRequest() {
  yield takeEvery(actionTypes.GET_VAT_INFO_REQUEST, getVatInfoRequestProcess);
}

function* issueTaxInvoiceRequest() {
  yield takeEvery(actionTypes.ISSUE_TAX_INVOICE_REQUEST, issueTaxInvoiceRequestProcess);
}

// Update Tax Invoice Saga
function* updateTaxInvoiceRequestProcess({ payload }: actionTypes.UpdateTaxInvoiceRequestAction): Generator<any, void, any> {
  try {

    // ดึง token จาก localStorage
    const { access_token } = yield call(getItems, ['access_token']);

    if (!access_token) {
      console.error('[Update Tax Invoice Saga] Access token is missing');
      yield put(actionCreators.updateTaxInvoiceError('Authentication token is missing. Please login again.'));
      return;
    }

    // ใช้ API URL สำหรับ update_tax_invoice
    const url = `${API_URL_V1}pms/rpc/salestax/update_tax_invoice`;

    // ใช้ API_PMS_HEADERS ตาม pattern ของ sagas อื่นๆ
    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;

    // Payload structure: { header, customer, summary, payment_type, interface, detail }
    // Note: vat_id is in payload.vat_id but API expects it in header.id
    const requestPayload = {
      header: payload.header,
      customer: payload.customer,
      summary: payload.summary,
      payment_type: payload.payment_type,
      interface: payload.interface,
      detail: payload.detail,
    };


    const response: any = yield call(APIPostRequest, url, requestPayload, API_PMS_HEADERS);

    // ตรวจสอบ response structure
    const responseData = response?.data || response;
    const hasResultCode = responseData?.result_code !== undefined;
    const resultCode = responseData?.result_code;

    // ตรวจสอบว่า response มี status หรือไม่
    const isSuccess = (response?.status === 200 || !response?.status) && resultCode === '0';

    if (isSuccess && hasResultCode) {
      // สร้าง UpdateTaxInvoiceResponse structure
      const updateResponse: actionTypes.UpdateTaxInvoiceResponse = {
        data: {
          result_code: responseData.result_code,
          result_msg: responseData.result_msg || 'Success',
          msg_code: responseData.msg_code || '00',
          result_data: responseData.result_data,
        },
        status: response?.status || 200,
        message: response?.message || 'OK',
      };

      yield put(actionCreators.updateTaxInvoiceSuccess(updateResponse));
    } else {
      // Error case
      console.error('[Update Tax Invoice Saga] Error - creating error response', {
        result_code: resultCode,
        result_msg: responseData?.result_msg,
        message: response?.message,
      });
      const errorMessage = responseData?.result_msg || response?.message || 'Failed to update tax invoice';

      yield put(actionCreators.updateTaxInvoiceError(errorMessage));

      if (response?.status === 403) {
        // Force signout if unauthorized
        yield put(userSignOut());
      }
    }
  } catch (error) {
    console.error('[Update Tax Invoice Saga] Exception occurred', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    yield put(actionCreators.updateTaxInvoiceError(errorMessage, error));
  }
}

function* cancelTaxInvoiceRequest() {
  yield takeEvery(actionTypes.CANCEL_TAX_INVOICE_REQUEST, cancelTaxInvoiceRequestProcess);
}

function* updateTaxInvoiceRequest() {
  yield takeEvery(actionTypes.UPDATE_TAX_INVOICE_REQUEST, updateTaxInvoiceRequestProcess);
}

// Get Active Tax Invoices Saga
function* getActiveTaxInvoicesRequestProcess({ payload }: actionTypes.GetActiveTaxInvoicesRequestAction): Generator<any, void, any> {
  try {
    // Check if SALESTAX is enabled before making API call
    const systemConfig: any = yield select((state: AppState) => state.systemConfig);
    const isSalesTaxEnabled = systemConfig?.dataSource?.interfaces?.SALESTAX === true;

    if (!isSalesTaxEnabled) {
      console.log('Get Active Tax Invoices API: SALESTAX is disabled, skipping API call');
      // Return empty success response instead of error to prevent UI issues
      yield put(actionCreators.getActiveTaxInvoicesSuccess({
        data: {
          result_code: '0',
          result_msg: 'SALESTAX is disabled',
          msg_code: '00',
          result_data: {
            items: [],
            paging: null
          }
        },
        status: 200,
        message: 'OK'
      }));
      return;
    }

    // ดึง token จาก localStorage
    const { access_token } = yield call(getItems, ['access_token']);

    if (!access_token) {
      console.error('Get Active Tax Invoices API: Access token is missing');
      yield put(actionCreators.getActiveTaxInvoicesError('Authentication token is missing. Please login again.'));
      return;
    }

    const { fromAdvanceButton, ...restPayload } = payload;

    // ใช้ API URL ตาม flag fromAdvanceButton
    const url = fromAdvanceButton
      ? `${API_URL_V1}pms/rpc/salestax/get_issued_tax_invoices`
      : `${API_URL_V1}pms/rpc/salestax/get_active_tax_invoices`;

    // ใช้ API_PMS_HEADERS ตาม pattern ของ sagas อื่นๆ
    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;

    let requestPayload: any;

    if (fromAdvanceButton) {
      // Build payload for get_issued_tax_invoices API
      const { authUser } = yield call(getItems, ['authUser']);
      const user_name = authUser?.user_name || '';

      // Ensure paging has default values if empty
      const pagingPayload = restPayload?.paging || {};
      const pageValue = pagingPayload.page ?? pagingPayload.current ?? 1;
      const limitValue = pagingPayload.limit || 20;
      const sortField = pagingPayload.sort_field || 'date, document';
      const sortOrder = pagingPayload.sort_order || 'desc';

      requestPayload = {
        user_name: user_name,
        platform: 'chrome',
        load_detail: false,
        bookno: null,
        booktype: null, // Set to null when opened from AdvanceButtonMenu
        docid: null,
        docno: restPayload.invoice_no || null,
        startdate: restPayload.start_date || null,
        enddate: restPayload.end_date || null,
        customer: restPayload.customer || null,
        taxid: null,
        reference: restPayload.folio_id ? String(restPayload.folio_id) : null, // Send folio_id as reference
        room_no: null,
        openby: null,
        cancelby: null,
        interface_from: null,
        paging: {
          page: pageValue,
          limit: limitValue,
          sort_field: sortField,
          sort_order: sortOrder
        }
      };

      console.log('[getIssuedTaxInvoices] Request payload:', JSON.stringify(requestPayload, null, 2));
    } else {
      // Original payload structure for get_active_tax_invoices
      const pagingPayload = restPayload?.paging || {};
      const pageValue = pagingPayload.page ?? pagingPayload.current ?? 1;

      requestPayload = {
        ...restPayload,
        paging: Object.keys(pagingPayload).length > 0
          ? { ...pagingPayload, page: pageValue }
          : { limit: 20, page: 1 }
      };

      // Remove current if it exists (use page instead)
      if ('current' in requestPayload.paging) {
        delete (requestPayload.paging as any).current;
      }

      console.log('[getActiveTaxInvoices] Request payload:', JSON.stringify(requestPayload, null, 2));
    }

    const response: any = yield call(APIPostRequest, url, requestPayload, API_PMS_HEADERS);

    // ตรวจสอบ response structure
    const responseData = response?.data || response;
    const hasResultCode = responseData?.result_code !== undefined;
    const hasResultData = responseData?.result_data !== undefined;
    const resultCode = responseData?.result_code;

    // ตรวจสอบว่า response มี status หรือไม่
    const isSuccess = (response?.status === 200 || !response?.status) && resultCode === '0';

    if (isSuccess && hasResultCode && hasResultData) {
      let items: any[] = [];
      let paging: any = null;

      if (fromAdvanceButton) {
        // Handle get_issued_tax_invoices response structure
        const resultData = responseData.result_data || {};
        const rawItems = resultData.items || [];

        // Map data structure from get_issued_tax_invoices to table format
        items = rawItems.map((item: any) => ({
          id: item.header?.id || null,
          invoice_no: item.header?.invoice_no || '',
          invoice_date: item.header?.invoice_date || '',
          customer: item.customer?.cust_name || '',
          reference: item.interface?.document_reference || '',
          vatable: item.summary?.vatable || 0,
          non_vat: item.summary?.nonvat || 0,
          vat_amt: item.summary?.valueaddtax || 0,
          total: item.summary?.total || 0,
          balance: item.invoice_balance || 0,
          // Keep original data for reference
          _original: item
        }));

        paging = resultData.paging || null;
      } else {
        // Handle original get_active_tax_invoices response structure
        const resultData = responseData.result_data || {};
        items = Array.isArray(resultData) ? resultData : (resultData.items || []);
        paging = resultData.paging || null;
      }

      const activeTaxInvoicesResponse: actionTypes.GetActiveTaxInvoicesResponse = {
        data: {
          result_code: responseData.result_code,
          result_msg: responseData.result_msg || 'Success',
          msg_code: responseData.msg_code || '00',
          result_data: {
            items: items,
            paging: paging,
          },
        },
        status: response?.status || 200,
        message: response?.message || 'OK',
      };

      yield put(actionCreators.getActiveTaxInvoicesSuccess(activeTaxInvoicesResponse));
    } else {
      // Error case
      const errorMessage = responseData?.result_msg || response?.message || 'Failed to get active tax invoices';

      yield put(actionCreators.getActiveTaxInvoicesError(errorMessage));

      if (response?.status === 403) {
        // Force signout if unauthorized
        yield put(userSignOut());
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    yield put(actionCreators.getActiveTaxInvoicesError(errorMessage, error));
  }
}

function* getActiveTaxInvoicesRequest() {
  yield takeEvery(actionTypes.GET_ACTIVE_TAX_INVOICES_REQUEST, getActiveTaxInvoicesRequestProcess);
}

function* verifyCreditNoteRequestProcess({ payload }: actionTypes.VerifyCreditNoteRequestAction): Generator<any, void, any> {
  try {
    // Check if SALESTAX is enabled before making API call
    const systemConfig: any = yield select((state: AppState) => state.systemConfig);
    const isSalesTaxEnabled = systemConfig?.dataSource?.interfaces?.SALESTAX === true;

    if (!isSalesTaxEnabled) {
      console.log('Verify Credit Note API: SALESTAX is disabled, skipping API call');
      yield put(actionCreators.verifyCreditNoteError('SALESTAX is disabled'));
      return;
    }

    const { access_token } = yield call(getItems, ['access_token']);
    if (!access_token) {
      yield put(actionCreators.verifyCreditNoteError('Authentication token is missing. Please login again.'));
      return;
    }
    const url = `${API_URL_V1}pms/rpc/salestax/verify_credit_note`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;
    const response: any = yield call(APIPostRequest, url, payload, API_PMS_HEADERS);

    const responseData = response?.data || response;
    const hasResultCode = responseData?.result_code !== undefined;
    const hasResultData = responseData?.result_data !== undefined;
    const resultCode = responseData?.result_code;

    const isSuccess = (response?.status === 200 || !response?.status) && resultCode === '0';

    if (isSuccess && hasResultCode && hasResultData) {
      const verifyCreditNoteResponse: actionTypes.GetVatInfoResponse = {
        data: responseData,
        status: response?.status || 200,
        message: response?.message || 'OK',
      };
      yield put(actionCreators.verifyCreditNoteSuccess(verifyCreditNoteResponse));
    } else {
      const errorMessage = responseData?.result_msg || response?.message || 'Failed to verify credit note';
      yield put(actionCreators.verifyCreditNoteError(errorMessage));
      if (response?.status === 403) {
        yield put(userSignOut());
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    yield put(actionCreators.verifyCreditNoteError(errorMessage, error));
  }
}

function* verifyCreditNoteRequest() {
  yield takeEvery(actionTypes.VERIFY_CREDIT_NOTE_REQUEST, verifyCreditNoteRequestProcess);
}

export default function* rootSaga() {
  yield all([
    fork(getVatInfoRequest),
    fork(issueTaxInvoiceRequest),
    fork(cancelTaxInvoiceRequest),
    fork(updateTaxInvoiceRequest),
    fork(getActiveTaxInvoicesRequest),
    fork(verifyCreditNoteRequest)
  ]);
}
